import * as Highcharts from 'highcharts';
import { getChartTheme } from './utils/chartTheme';
import DataTable from 'datatables.net-bs5';
import { post, notifier } from './util';

// Injected by Oura/default.latte via {json_encode($trends)|noescape}
declare const oura_trends: {
    sleep_score: [number, number | null][],  // [timestamp_ms, value]
    readiness:   [number, number | null][],
    hrv:         [number, number | null][],
    resting_hr:  [number, number | null][],
    steps:       [number, number | null][],
    // NEW Phase 185: sleep architecture (seconds in DB, h:mm in tooltip)
    deep_sleep:          [number, number | null][],
    rem_sleep:           [number, number | null][],
    light_sleep:         [number, number | null][],
    awake_time:          [number, number | null][],
    // NEW Phase 185: sleep quality
    efficiency:          [number, number | null][],
    latency:             [number, number | null][],
    total_sleep_duration:[number, number | null][],
    // NEW Phase 185: activity depth
    activity_score:      [number, number | null][],
    active_calories:     [number, number | null][],
    total_calories:      [number, number | null][],
    // NEW Phase 185: stress vs recovery + resilience
    stress_high:         [number, number | null][],
    recovery_high:       [number, number | null][],
    resilience_ord:      [number, number | null][],
    // NEW Phase 185: vitals
    average_breath:      [number, number | null][],
    // temperature_deviation: normal nights as [ts, float|null]; illness nights (>=1.0°C)
    // as point objects with marker override (PHP emits mixed format, D-10/D-11)
    temperature_deviation: (number | null | { x: number, y: number, marker: object })[],
    // NEW Phase 189 OEND-01/D-02: SpO2 + BDI (breathing disturbance index)
    spo2_avg: [number, number | null][],
    bdi:      [number, number | null][],   // breathing_disturbance_index [0,100]
    // NEW Phase 282 QUICK-02/D-07: vo2_max + pulse_wave_velocity
    vo2_max:             [number, number | null][],   // mL/kg/min, dead column since Phase 161
    pulse_wave_velocity: [number, number | null][],   // m/s
};

// NEW Phase 185: bedtime data from oura_sleep_periods (separate source)
// Injected by Oura/default.latte via {json_encode($bedtimes)|noescape}
declare const oura_bedtimes: {
    bedtime_start: [number, number | null][],
    bedtime_end:   [number, number | null][],
};

// NEW Phase 188 OTAG-02: tag annotation bands — injected by Oura/default.latte
// {json_encode($tag_bands)|noescape} — all three categories always present (empty array when no tags)
declare const oura_tag_bands: {
    illness:  Array<{ from_ms: number; to_ms: number; comment: string | null }>;
    alcohol:  Array<{ from_ms: number; to_ms: number; comment: string | null }>;
    caffeine: Array<{ from_ms: number; to_ms: number; comment: string | null }>;
};

// NEW Phase 189 OEND-03/D-07/D-08: sessions DataTable data
// Injected by Oura/default.latte via {json_encode($sessions)|noescape}
// mood = 5-value PublicMomentMood (bad/worse/same/good/great) or null
declare const oura_sessions: Array<{
    day:      string,
    type:     string,
    duration: number,   // minutes
    mood:     string | null,
}>;

// NEW Phase 224 OURA-04/D-05/D-06/D-08: last-24h intraday heart rate series.
// Injected by Oura/default.latte via {json_encode($intradayHr)|noescape} — array of
// [timestamp_ms, bpm] pairs matching every other series' epoch-ms convention.
declare const oura_intraday_hr: [number, number][];

// NEW Phase 224 OURA-05/D-11: rest_mode recovery annotation bands — flat array (single
// "recovery" band type, no illness/alcohol/caffeine sub-categorization like oura_tag_bands).
// Injected by Oura/default.latte via {json_encode($restModeBands)|noescape}.
declare const oura_rest_mode_bands: Array<{ from_ms: number; to_ms: number; comment: string | null }>;

// Resilience ordinal → word label (D-07b — word labels on y-axis + tooltip, not numbers)
// Declare at module scope to avoid function name collision (feedback_js_chart_fn_global_var_name_collision)
const RESILIENCE_LABELS: Record<number, string> = {
    1: 'Limited',
    2: 'Adequate',
    3: 'Solid',
    4: 'Strong',
    5: 'Exceptional',
};

// Phase 224 OURA-03/D-03: accordion collapse-state persistence. Module-scope keys +
// idempotency guard (feedback_shared_component_every_page_init_double_bind) — bind the
// document-level Bootstrap Collapse listeners at most once even if oura() re-runs.
const OURA_SECTION_KEYS = ['sleep', 'activity', 'vitals', 'workouts', 'sessions'];
let collapsePersistenceBound = false;

export default class {
    static init = () => {
        oura();
    }
}

function oura(): void {
    // Guard: oura_trends is only injected on the /oura page (Pitfall 6).
    if (typeof oura_trends === 'undefined') {
        return;
    }
    // Guard: oura_bedtimes is a separate global injected alongside oura_trends.
    // Must be checked independently — if the template is out of date, bail before
    // any chart that references oura_bedtimes (Pitfall 6).
    if (typeof oura_bedtimes === 'undefined') {
        return;
    }
    // Guard: oura_tag_bands — Phase 188 OTAG-02. Template injects this; bail if absent
    // (e.g. older cached template version). All categories are always present as arrays.
    if (typeof oura_tag_bands === 'undefined') {
        return;
    }
    // Guard: oura_sessions — Phase 189 OEND-03. Template injects this as a JSON array.
    // May be an empty array (0 rows in oura_sessions) — that is a valid state, not an error.
    if (typeof oura_sessions === 'undefined') {
        return;
    }
    // Guard: oura_intraday_hr — Phase 224 OURA-04. May be an empty array (no heartrate rows
    // in the last 24h) — that is a valid state, not an error.
    if (typeof oura_intraday_hr === 'undefined') {
        return;
    }
    // Guard: oura_rest_mode_bands — Phase 224 OURA-05. May be an empty array (no rest_mode
    // periods in the lookback window, or pre-migration) — that is a valid state, not an error.
    if (typeof oura_rest_mode_bands === 'undefined') {
        return;
    }

    // -------------------------------------------------------------------------
    // Phase 188 OTAG-02: tag annotation plotBands builder
    // Called once per annotated chart (Sleep + HRV/resting-HR + Vitals — D-07).
    // Colors: illness=red, alcohol=amber, caffeine=blue (semi-transparent ~0.15 alpha).
    // CHRT-03: band colors live HERE in per-chart xAxis.plotBands, NOT in getChartTheme().
    // Empty arrays (no tags logged yet) produce an empty plotBands array — Highcharts no-ops.
    // -------------------------------------------------------------------------
    const tagBands = (): Highcharts.XAxisPlotBandsOptions[] => [
        ...oura_tag_bands.illness.map(b => ({
            from: b.from_ms,
            to:   b.to_ms,
            color: 'rgba(239, 68, 68, 0.15)',   // red (illness)
            zIndex: 0,
        } as Highcharts.XAxisPlotBandsOptions)),
        ...oura_tag_bands.alcohol.map(b => ({
            from: b.from_ms,
            to:   b.to_ms,
            color: 'rgba(245, 158, 11, 0.15)',  // amber (alcohol)
            zIndex: 0,
        } as Highcharts.XAxisPlotBandsOptions)),
        ...oura_tag_bands.caffeine.map(b => ({
            from: b.from_ms,
            to:   b.to_ms,
            color: 'rgba(96, 165, 250, 0.15)',  // blue (caffeine)
            zIndex: 0,
        } as Highcharts.XAxisPlotBandsOptions)),
        // Phase 224 OURA-05/D-11: rest_mode recovery windows — reuses the same tag-band
        // plotBands mechanism verbatim. Teal is distinct from illness(red)/alcohol(amber)/
        // caffeine(blue) so a rest-mode window never gets misread as one of the other tags.
        ...oura_rest_mode_bands.map(b => ({
            from: b.from_ms,
            to:   b.to_ms,
            color: 'rgba(20, 184, 166, 0.15)',  // teal (rest mode / recovery)
            zIndex: 0,
            ...(b.comment ? { label: { text: b.comment } } : {}),
        } as Highcharts.XAxisPlotBandsOptions)),
    ];

    // -------------------------------------------------------------------------
    // Chart 1: Sleep Score (existing)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-sleep-score')) {
        Highcharts.chart('chart-sleep-score', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Sleep Score', style: { fontSize: '14px' } },
            xAxis: {
                type: 'datetime',
                plotBands: tagBands(),  // Phase 188 OTAG-02
            },
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,  // MANDATORY: sparse dates show as gaps, not interpolated
                },
            },
            series: [{
                type: 'line',
                name: 'Sleep Score',
                data: oura_trends.sleep_score,
                color: '#3BABD3',
            }],
            tooltip: {
                valueDecimals: 0,
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // -------------------------------------------------------------------------
    // Chart 2: Readiness Score (existing)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-readiness')) {
        Highcharts.chart('chart-readiness', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Readiness Score', style: { fontSize: '14px' } },
            xAxis: {
                type: 'datetime',
                plotBands: tagBands(),  // Phase 188 OTAG-02
            },
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,
                },
            },
            series: [{
                type: 'line',
                name: 'Readiness Score',
                data: oura_trends.readiness,
                color: '#0090FF',
            }],
            tooltip: {
                valueDecimals: 0,
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // -------------------------------------------------------------------------
    // Chart 3: Heart Rate Variability (existing)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-hrv')) {
        Highcharts.chart('chart-hrv', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Heart Rate Variability', style: { fontSize: '14px' } },
            xAxis: {
                type: 'datetime',
                plotBands: tagBands(),  // Phase 188 OTAG-02
            },
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,
                },
            },
            series: [{
                type: 'line',
                name: 'HRV',
                data: oura_trends.hrv,
                color: '#BB72F4',
            }],
            tooltip: {
                valueDecimals: 1,
                valueSuffix: ' ms',
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // -------------------------------------------------------------------------
    // Chart 4: Resting Heart Rate (existing)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-resting-hr')) {
        Highcharts.chart('chart-resting-hr', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Resting Heart Rate', style: { fontSize: '14px' } },
            xAxis: {
                type: 'datetime',
                plotBands: tagBands(),  // Phase 188 OTAG-02
            },
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,
                },
            },
            series: [{
                type: 'line',
                name: 'Resting HR',
                data: oura_trends.resting_hr,
                color: '#FF5733',
            }],
            tooltip: {
                valueDecimals: 0,
                valueSuffix: ' bpm',
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // -------------------------------------------------------------------------
    // Chart 5: Daily Steps (existing)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-steps')) {
        Highcharts.chart('chart-steps', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Daily Steps', style: { fontSize: '14px' } },
            xAxis: {
                type: 'datetime',
                plotBands: tagBands(),  // Phase 188 OTAG-02
            },
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,
                },
            },
            series: [{
                type: 'line',
                name: 'Steps',
                data: oura_trends.steps,
                color: '#4CAF50',
            }],
            tooltip: {
                valueDecimals: 0,
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // =========================================================================
    // NEW Phase 185 — Sleep Section Charts (OSURF-01, OSURF-02, OSURF-03)
    // =========================================================================

    // -------------------------------------------------------------------------
    // Chart 6: Sleep Architecture — stacked area, 4 stages (OSURF-01, D-01)
    // NOT 100%-stacked — absolute hours must remain visible (D-01).
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-sleep-architecture')) {
        Highcharts.chart('chart-sleep-architecture', Highcharts.merge(getChartTheme(), {
            chart: { type: 'area', height: 320 },
            title: { text: 'Sleep Architecture', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime', plotBands: tagBands() },  // Phase 188 OTAG-02
            yAxis: {
                title: { text: 'Hours' },
                gridLineWidth: 0.1,
                labels: {
                    formatter: function (this: any): string {
                        return (this.value / 3600).toFixed(0) + 'h';
                    },
                },
            },
            legend: { enabled: true },
            plotOptions: {
                area: {
                    stacking: 'normal',  // absolute hours, NOT '100%' (D-01)
                    marker: { enabled: false },
                    lineWidth: 1,
                    fillOpacity: 0.6,
                    connectNulls: false,
                },
            },
            tooltip: {
                shared: true,
                // CHRT-03: pointFormatter ONLY — tooltip.formatter is FORBIDDEN
                pointFormatter: function (this: Highcharts.Point): string {
                    if (this.y === null || this.y === undefined) return '';
                    const secs = this.y as number;
                    const h = Math.floor(secs / 3600);
                    const m = Math.floor((secs % 3600) / 60);
                    return `<span style="color:${(this as any).color}">●</span> ${this.series.name}: <b>${h}h ${String(m).padStart(2, '0')}m</b><br/>`;
                },
            },
            series: [
                { type: 'area', name: 'Deep',  data: oura_trends.deep_sleep,  color: '#3B82F6' },
                { type: 'area', name: 'REM',   data: oura_trends.rem_sleep,   color: '#8B5CF6' },
                { type: 'area', name: 'Light', data: oura_trends.light_sleep, color: '#60A5FA' },
                { type: 'area', name: 'Awake', data: oura_trends.awake_time,  color: '#F59E0B' },
            ],
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // -------------------------------------------------------------------------
    // Chart 7: Sleep Efficiency (OSURF-02, part 1)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-sleep-efficiency')) {
        Highcharts.chart('chart-sleep-efficiency', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Sleep Efficiency', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime', plotBands: tagBands() },  // Phase 188 OTAG-02
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
                labels: {
                    formatter: function (this: any): string {
                        return this.value + '%';
                    },
                },
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,
                },
            },
            series: [{
                type: 'line',
                name: 'Efficiency',
                data: oura_trends.efficiency,
                color: '#10B981',
            }],
            tooltip: {
                pointFormatter: function (this: Highcharts.Point): string {
                    if (this.y === null || this.y === undefined) return '';
                    return `<span style="color:${(this as any).color}">●</span> Efficiency: <b>${this.y}%</b><br/>`;
                },
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // -------------------------------------------------------------------------
    // Chart 8: Sleep Latency (OSURF-02, part 2)
    // Stored as seconds; display as minutes (NOT h:mm — latency is short, D-07a)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-sleep-latency')) {
        Highcharts.chart('chart-sleep-latency', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Sleep Latency', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime', plotBands: tagBands() },  // Phase 188 OTAG-02
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
                labels: {
                    formatter: function (this: any): string {
                        return Math.round(this.value / 60) + 'm';
                    },
                },
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,
                },
            },
            series: [{
                type: 'line',
                name: 'Latency',
                data: oura_trends.latency,
                color: '#A78BFA',
            }],
            tooltip: {
                pointFormatter: function (this: Highcharts.Point): string {
                    if (this.y === null || this.y === undefined) return '';
                    const mins = Math.round((this.y as number) / 60);
                    return `<span style="color:${(this as any).color}">●</span> Latency: <b>${mins} min</b><br/>`;
                },
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // -------------------------------------------------------------------------
    // Chart 9: Total Sleep Duration (OSURF-02, part 3)
    // Stored as seconds; display as h:mm (D-07a)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-total-sleep')) {
        Highcharts.chart('chart-total-sleep', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Total Sleep Duration', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime', plotBands: tagBands() },  // Phase 188 OTAG-02
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
                labels: {
                    formatter: function (this: any): string {
                        return (this.value / 3600).toFixed(1) + 'h';
                    },
                },
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,
                },
            },
            series: [{
                type: 'line',
                name: 'Total Sleep',
                data: oura_trends.total_sleep_duration,
                color: '#60A5FA',
            }],
            tooltip: {
                pointFormatter: function (this: Highcharts.Point): string {
                    if (this.y === null || this.y === undefined) return '';
                    const secs = this.y as number;
                    const h = Math.floor(secs / 3600);
                    const m = Math.floor((secs % 3600) / 60);
                    return `<span style="color:${(this as any).color}">●</span> Total Sleep: <b>${h}h ${String(m).padStart(2, '0')}m</b><br/>`;
                },
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // -------------------------------------------------------------------------
    // Chart 10: Bedtime Consistency (OSURF-03, D-03, D-16)
    // Y-axis = minutes from 8 PM anchor (PHP pre-encoded; post-midnight += 1440)
    // Clock formatter wraps via % 1440 to show 12-hr clock strings.
    // Do NOT set per-chart time.useUTC — global default handles it (feedback_highcharts_useutc_default)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-bedtime')) {
        Highcharts.chart('chart-bedtime', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Bedtime Consistency', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime', plotBands: tagBands() },  // Phase 188 OTAG-02
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
                // 8 PM anchor = 1200 min; min = 6 PM safety margin; max = 4 AM next day (D-16)
                min: 18 * 60,
                max: 28 * 60,
                labels: {
                    formatter: function (this: any): string {
                        const mins = (this.value as number) % 1440;
                        const h = Math.floor(mins / 60);
                        const m = mins % 60;
                        const ampm = h >= 12 ? 'PM' : 'AM';
                        const h12 = h % 12 || 12;
                        return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
                    },
                },
            },
            legend: { enabled: true },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: true, radius: 3 },
                    connectNulls: false,
                },
            },
            series: [
                {
                    type: 'line',
                    name: 'Bedtime',
                    data: oura_bedtimes.bedtime_start,
                    color: '#60A5FA',
                },
                {
                    type: 'line',
                    name: 'Wake',
                    data: oura_bedtimes.bedtime_end,
                    color: '#F59E0B',
                },
            ],
            tooltip: {
                shared: true,
                pointFormatter: function (this: Highcharts.Point): string {
                    if (this.y === null || this.y === undefined) return '';
                    const mins = (this.y as number) % 1440;
                    const h = Math.floor(mins / 60);
                    const m = mins % 60;
                    const ampm = h >= 12 ? 'PM' : 'AM';
                    const h12 = h % 12 || 12;
                    return `<span style="color:${(this as any).color}">●</span> ${this.series.name}: <b>${h12}:${String(m).padStart(2, '0')} ${ampm}</b><br/>`;
                },
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // =========================================================================
    // NEW Phase 185 — Activity Section Charts (OSURF-04)
    // =========================================================================

    // -------------------------------------------------------------------------
    // Chart 11: Activity Depth — dual-axis combo (OSURF-04, D-04)
    // Primary (left): activity score 0-100 line
    // Secondary (right, opposite): calories columns
    // CRITICAL: Do NOT add labels.style/title.style to secondary yAxis —
    // that overrides the Phase-183 global setOptions and breaks re-theme (Pitfall 5).
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-activity')) {
        Highcharts.chart('chart-activity', Highcharts.merge(getChartTheme(), {
            chart: { height: 320 },
            title: { text: 'Activity Depth', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime' },
            yAxis: [
                {
                    // Primary (left): activity score 0-100
                    title: { text: 'Score' },
                    gridLineWidth: 0.1,
                    min: 0,
                    max: 100,
                },
                {
                    // Secondary (right): calories — NO labels.style/title.style (Pitfall 5)
                    title: { text: 'Calories' },
                    gridLineWidth: 0,
                    opposite: true,
                },
            ],
            legend: { enabled: true },
            plotOptions: {
                line:   { lineWidth: 2, marker: { enabled: false }, connectNulls: false },
                column: { connectNulls: false },
            },
            series: [
                {
                    type: 'line',
                    name: 'Activity Score',
                    data: oura_trends.activity_score,
                    yAxis: 0,
                    color: '#10B981',
                },
                {
                    type: 'column',
                    name: 'Active Cal',
                    data: oura_trends.active_calories,
                    yAxis: 1,
                    color: '#F59E0B',
                },
                {
                    type: 'column',
                    name: 'Total Cal',
                    data: oura_trends.total_calories,
                    yAxis: 1,
                    color: '#6B7280',
                    opacity: 0.4,
                },
            ],
            tooltip: {
                shared: true,
                pointFormatter: function (this: Highcharts.Point): string {
                    if (this.y === null || this.y === undefined) return '';
                    return `<span style="color:${(this as any).color}">●</span> ${this.series.name}: <b>${this.y}</b><br/>`;
                },
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // =========================================================================
    // NEW Phase 185 — Vitals Section Charts (OSURF-05, OSURF-06)
    // =========================================================================

    // -------------------------------------------------------------------------
    // Chart 12: Stress vs Recovery (OSURF-05, part 1, D-05)
    // Paired lines: stress high (minutes) vs recovery high (minutes)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-stress-recovery')) {
        Highcharts.chart('chart-stress-recovery', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Stress vs Recovery', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime', plotBands: tagBands() },  // Phase 188 OTAG-02
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
                labels: {
                    formatter: function (this: any): string {
                        return this.value + ' min';
                    },
                },
            },
            legend: { enabled: true },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,
                },
            },
            series: [
                {
                    type: 'line',
                    name: 'Stress',
                    data: oura_trends.stress_high,
                    color: '#EF4444',
                },
                {
                    type: 'line',
                    name: 'Recovery',
                    data: oura_trends.recovery_high,
                    color: '#10B981',
                },
            ],
            tooltip: {
                shared: true,
                pointFormatter: function (this: Highcharts.Point): string {
                    if (this.y === null || this.y === undefined) return '';
                    return `<span style="color:${(this as any).color}">●</span> ${this.series.name}: <b>${this.y} min</b><br/>`;
                },
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // -------------------------------------------------------------------------
    // Chart 13: Resilience Trend (OSURF-05, part 2, D-07b)
    // Y-axis: ordinal 1–5 with WORD labels (not numbers).
    // Tooltip also shows the word. (D-07b mandate)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-resilience')) {
        Highcharts.chart('chart-resilience', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Resilience Level', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime', plotBands: tagBands() },  // Phase 188 OTAG-02
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
                min: 1,
                max: 5,
                tickPositions: [1, 2, 3, 4, 5],
                labels: {
                    formatter: function (this: any): string {
                        return RESILIENCE_LABELS[this.value] ?? '';
                    },
                },
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: true, radius: 4 },
                    connectNulls: false,
                },
            },
            series: [{
                type: 'line',
                name: 'Resilience',
                data: oura_trends.resilience_ord,
                color: '#A78BFA',
            }],
            tooltip: {
                pointFormatter: function (this: Highcharts.Point): string {
                    if (this.y === null || this.y === undefined) return '';
                    const word = RESILIENCE_LABELS[this.y as number] ?? String(this.y);
                    return `<span style="color:${(this as any).color}">●</span> Resilience: <b>${word}</b><br/>`;
                },
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // -------------------------------------------------------------------------
    // Chart 14: Respiratory Rate (OSURF-06, D-06)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-respiratory-rate')) {
        Highcharts.chart('chart-respiratory-rate', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Respiratory Rate', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime', plotBands: tagBands() },  // Phase 188 OTAG-02
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
                labels: {
                    formatter: function (this: any): string {
                        return (this.value as number).toFixed(1) + ' brpm';
                    },
                },
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,
                },
            },
            series: [{
                type: 'line',
                name: 'Respiratory Rate',
                data: oura_trends.average_breath,
                color: '#34D399',
            }],
            tooltip: {
                pointFormatter: function (this: Highcharts.Point): string {
                    if (this.y === null || this.y === undefined) return '';
                    return `<span style="color:${(this as any).color}">●</span> Respiratory Rate: <b>${(this.y as number).toFixed(1)} brpm</b><br/>`;
                },
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // =========================================================================
    // NEW Phase 189 — SpO2 chart (OEND-01/D-02)
    // =========================================================================

    // -------------------------------------------------------------------------
    // Chart 16: Blood Oxygen (SpO2) — line chart, yAxis 90-100% (physiological range)
    // connectNulls:false — Oura only reports SpO2 on days with sleep data; sparse = gaps.
    // getChartTheme merge — re-themes on dark/light toggle (D-17).
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-spo2')) {
        Highcharts.chart('chart-spo2', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Blood Oxygen (SpO2)', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime' },
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
                min: 90,
                max: 100,
                labels: {
                    formatter: function (this: any): string {
                        return this.value + '%';
                    },
                },
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,  // MANDATORY — sparse dates show as gaps
                },
            },
            series: [{
                type: 'line',
                name: 'SpO2 %',
                data: oura_trends.spo2_avg,
                color: '#3BABD3',
            }],
            tooltip: { valueDecimals: 1, valueSuffix: '%' },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // =========================================================================
    // NEW Phase 224 — Intraday Heart Rate (OURA-04/D-05/D-06/D-08)
    // =========================================================================

    // -------------------------------------------------------------------------
    // Chart 17: Intraday Heart Rate — Last 24h. The "hourly freshness pays off"
    // showcase — last night's resting-HR dip + today's daytime activity, current to
    // within the hour thanks to OURA-01's hourly cron cadence.
    // Do NOT add a per-chart time.useUTC block — global default handles it
    // (feedback_highcharts_useutc_default).
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-intraday-hr')) {
        Highcharts.chart('chart-intraday-hr', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Heart Rate — Last 24h', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime' },
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 1.5,
                    marker: { enabled: false },
                    connectNulls: false,
                },
            },
            series: [{
                type: 'line',
                name: 'Heart Rate',
                data: oura_intraday_hr,
                color: '#FF5733',
            }],
            tooltip: { valueSuffix: ' bpm' },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // =========================================================================
    // NEW Phase 189 — Sessions DataTable (OEND-03/D-07/D-08)
    // =========================================================================

    // -------------------------------------------------------------------------
    // Sessions DataTable init — client-side (data pre-rendered in PHP via $sessions array).
    // No serverSide/ajax — dataset is small (tens to hundreds of sessions per 90 days).
    // DataTable only init'd when the table element exists AND sessions array is non-empty
    // (empty state renders a <p> instead of the <table> — guarded in default.latte).
    // -------------------------------------------------------------------------
    const sessionsTable = document.querySelector('[data-table="oura_sessions"]') as HTMLTableElement | null;
    if (sessionsTable) {
        new DataTable(sessionsTable, {
            pageLength: 25,
            order: [[0, 'desc']],   // date DESC — most recent session first
        });
    }

    // -------------------------------------------------------------------------
    // Chart 15: Body Temperature Deviation (OSURF-06, D-06, D-10, D-11)
    // Zones (zoneAxis: 'y'): blue normal < +0.5°C, amber watch +0.5–1.0°C, red illness >= +1.0°C
    // PlotLines: baseline 0°C, +0.5°C Watch, +1.0°C Illness
    // Illness nights emitted as point objects by PHP (marker radius 6, fillColor red)
    // Use conditional-spread to avoid explicit undefined values (feedback_highcharts_undefined_options)
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-temp-deviation')) {
        Highcharts.chart('chart-temp-deviation', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Body Temperature Deviation', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime', plotBands: tagBands() },  // Phase 188 OTAG-02
            yAxis: {
                title: { text: '°C from baseline' },
                gridLineWidth: 0.1,
                labels: {
                    formatter: function (this: any): string {
                        return (this.value as number).toFixed(2) + '°C';
                    },
                },
                plotLines: [
                    {
                        value: 0,
                        color: 'rgba(255,255,255,0.2)',
                        width: 1,
                        dashStyle: 'Dot',
                    },
                    {
                        value: 0.5,
                        color: '#F59E0B',
                        width: 1,
                        dashStyle: 'ShortDash',
                        label: { text: 'Watch' },
                    },
                    {
                        value: 1.0,
                        color: '#EF4444',
                        width: 1,
                        dashStyle: 'ShortDash',
                        label: { text: 'Illness' },
                    },
                ],
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    // marker.enabled: true radius:3 — illness nights carry their own bigger marker
                    // object from PHP side; do not fight it (per plan action spec)
                    marker: { enabled: true, radius: 3 },
                    connectNulls: false,
                    // Zones color the line segment by y threshold (zoneAxis: 'y')
                    zones: [
                        { value: 0.5, color: '#60A5FA' },  // normal: blue
                        { value: 1.0, color: '#F59E0B' },  // watch: amber
                        { color: '#EF4444' },              // illness: red
                    ],
                    zoneAxis: 'y',
                },
            },
            series: [{
                type: 'line',
                name: 'Temp Deviation',
                data: oura_trends.temperature_deviation as any[],
            }],
            tooltip: {
                pointFormatter: function (this: Highcharts.Point): string {
                    if (this.y === null || this.y === undefined) return '';
                    const v = this.y as number;
                    const flag = v >= 1.0 ? ' ⚠ illness threshold' : v >= 0.5 ? ' · watch' : '';
                    return `<span style="color:${(this as any).color}">●</span> Temp deviation: <b>${v >= 0 ? '+' : ''}${v.toFixed(2)}°C</b>${flag}<br/>`;
                },
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // =========================================================================
    // NEW Phase 282 QUICK-02/D-07 — VO2 Max + Pulse Wave Velocity trend charts
    // =========================================================================

    // -------------------------------------------------------------------------
    // Chart 18: VO2 Max — vo2_max has been a dead column since Phase 161, so its
    // historical density is unknown. connectNulls: false renders any gap honestly
    // rather than interpolating data that does not exist (D-07).
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-vo2-max')) {
        Highcharts.chart('chart-vo2-max', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'VO2 Max', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime', plotBands: tagBands() },  // Phase 188 OTAG-02
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
                labels: {
                    formatter: function (this: any): string {
                        return (this.value as number).toFixed(1) + ' mL/kg/min';
                    },
                },
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,
                },
            },
            series: [{
                type: 'line',
                name: 'VO2 Max',
                data: oura_trends.vo2_max,
                color: '#FBBF24',
            }],
            tooltip: {
                pointFormatter: function (this: Highcharts.Point): string {
                    if (this.y === null || this.y === undefined) return '';
                    return `<span style="color:${(this as any).color}">●</span> VO2 Max: <b>${(this.y as number).toFixed(1)} mL/kg/min</b><br/>`;
                },
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // -------------------------------------------------------------------------
    // Chart 19: Pulse Wave Velocity — connectNulls: false per D-07, same reasoning
    // as the VO2 Max chart above.
    // -------------------------------------------------------------------------
    if (document.getElementById('chart-pulse-wave-velocity')) {
        Highcharts.chart('chart-pulse-wave-velocity', Highcharts.merge(getChartTheme(), {
            chart: { type: 'line', height: 320 },
            title: { text: 'Pulse Wave Velocity', style: { fontSize: '14px' } },
            xAxis: { type: 'datetime', plotBands: tagBands() },  // Phase 188 OTAG-02
            yAxis: {
                title: { text: null },
                gridLineWidth: 0.1,
                labels: {
                    formatter: function (this: any): string {
                        return (this.value as number).toFixed(1) + ' m/s';
                    },
                },
            },
            legend: { enabled: false },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    marker: { enabled: false },
                    connectNulls: false,
                },
            },
            series: [{
                type: 'line',
                name: 'Pulse Wave Velocity',
                data: oura_trends.pulse_wave_velocity,
                color: '#818CF8',
            }],
            tooltip: {
                pointFormatter: function (this: Highcharts.Point): string {
                    if (this.y === null || this.y === undefined) return '';
                    return `<span style="color:${(this as any).color}">●</span> Pulse Wave Velocity: <b>${(this.y as number).toFixed(1)} m/s</b><br/>`;
                },
            },
            credits: { enabled: false },
        } as unknown as Highcharts.Options));
    }

    // =========================================================================
    // NEW Phase 224 — Accordion collapse-state persistence (OURA-03/D-01/D-03)
    // =========================================================================

    // -------------------------------------------------------------------------
    // Bootstrap's data-api (bundled globally via shared.ts's `import * as bootstrap
    // from "bootstrap"`) already drives the toggle itself — every accordion-button
    // carries data-bs-toggle="collapse", so no Collapse instance needs to be created
    // here. This block only PERSISTS the resulting open/close state server-side (the
    // settings table, per-user, cross-device — D-03 explicitly rejects localStorage,
    // unlike sidebar.ts's group-collapse persistence). Mirrors sortable.ts's
    // saveOrder() POST shape exactly: post('?do=saveSectionsCollapsed&collapsed=...').
    // -------------------------------------------------------------------------
    if (!collapsePersistenceBound) {
        collapsePersistenceBound = true;

        const persistCollapsedState = (): void => {
            const collapsed = OURA_SECTION_KEYS.filter((key) => {
                const body = document.getElementById(`acc-${key}-body`);
                return body !== null && !body.classList.contains('show');
            });
            post('?do=saveSectionsCollapsed&collapsed=' + encodeURIComponent(collapsed.join(',')), {}, (data: any) => {
                if (data.status !== 1) {
                    notifier.alert('Failed to save section state');
                }
            });
        };

        document.addEventListener('shown.bs.collapse', (e: Event) => {
            const el = e.target as HTMLElement;
            if (!/^acc-.+-body$/.test(el.id)) return;
            persistCollapsedState();
        });

        document.addEventListener('hidden.bs.collapse', (e: Event) => {
            const el = e.target as HTMLElement;
            if (!/^acc-.+-body$/.test(el.id)) return;
            persistCollapsedState();
        });
    }
}
